import { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { photoStorage, getCategoryIcon } from '../lib/photoStorage';

// Simple in-memory cache for photos to avoid repeated IndexedDB reads
const photoCache = new Map();

export default function ProductPhoto({ 
  productId, 
  backendImage,
  categoryName,
  categoryIcon,
  size = 'sm', // 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className = '',
  showFallbackIcon = true,
  aspectRatio = 'square' // 'square' | '16/9'
}) {
  const [photo, setPhoto] = useState(backendImage || photoCache.get(productId) || null);
  const [loading, setLoading] = useState(!backendImage && !photoCache.has(productId));

  useEffect(() => {
    if (backendImage) {
      setPhoto(backendImage);
      photoCache.set(productId, backendImage);
      setLoading(false);
      return;
    }

    if (photoCache.has(productId)) {
      setPhoto(photoCache.get(productId));
      setLoading(false);
      return;
    }

    let mounted = true;
    
    photoStorage.getPhoto(productId).then((photoData) => {
      if (mounted) {
        photoCache.set(productId, photoData);
        setPhoto(photoData);
        setLoading(false);
      }
    }).catch(() => {
      if (mounted) {
        setLoading(false);
      }
    });

    return () => { mounted = false; };
  }, [productId, backendImage]);

  const sizeClasses = {
    xs: 'w-8 h-8',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
    full: 'w-full'
  };

  const iconSizes = {
    xs: 'text-lg',
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl',
    full: 'text-4xl'
  };

  const aspectClass = aspectRatio === '16/9' ? 'aspect-video' : 'aspect-square';
  // Use custom category icon if provided, otherwise fallback to predefined icons
  const displayIcon = getCategoryIcon(categoryName, categoryIcon);

  if (loading) {
    return (
      <div className={`${sizeClasses[size]} ${aspectClass} rounded-lg bg-secondary animate-pulse flex items-center justify-center ${className}`}>
        <Package className="w-1/2 h-1/2 text-muted-foreground/30" />
      </div>
    );
  }

  if (photo) {
    return (
      <div className={`${sizeClasses[size]} ${aspectClass} rounded-lg overflow-hidden bg-secondary ${className}`}>
        <img 
          src={photo} 
          alt="" 
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  // Fallback: category icon
  if (showFallbackIcon) {
    return (
      <div className={`${sizeClasses[size]} ${aspectClass} rounded-lg bg-secondary/50 flex items-center justify-center ${className}`}>
        <span className={iconSizes[size]}>{displayIcon}</span>
      </div>
    );
  }

  return null;
}

// Hook to get a single photo
export function useProductPhoto(productId) {
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    photoStorage.getPhoto(productId).then((photoData) => {
      setPhoto(photoData);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [productId]);

  return { photo, loading };
}

// Hook to get multiple photos
export function useProductPhotos(productIds) {
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productIds || productIds.length === 0) {
      setLoading(false);
      return;
    }

    photoStorage.getPhotos(productIds).then((photosData) => {
      setPhotos(photosData);
      // Update cache
      Object.entries(photosData).forEach(([id, photo]) => {
        photoCache.set(id, photo);
      });
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [productIds?.join(',')]);

  return { photos, loading };
}

// Clear photo cache (useful when updating photos)
export function clearPhotoCache(productId = null) {
  if (productId) {
    photoCache.delete(productId);
  } else {
    photoCache.clear();
  }
}
